/**
 * Document Viewer Component
 *
 * Displays uploaded documents (PDFs and images) in the left pane
 * of the PO Review interface. This is the "source of truth" that
 * users compare against AI-extracted data.
 *
 * Features:
 * - PDF viewing with page navigation
 * - Zoom controls
 * - Image viewing for non-PDF documents
 * - Loading and error states
 */

import { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { getPreviewText } from '../../api/fileApi';

// Configure PDF.js worker
// This is required for react-pdf to parse PDF documents
// We use a CDN-hosted worker to avoid bundling issues
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * DocumentViewer Component
 *
 * @param {Object} props - Component props
 * @param {string} props.url - URL of the document to display
 * @param {string} props.mimeType - MIME type of the document
 *
 * @example
 * <DocumentViewer
 *   url="http://localhost:3000/uploads/po-123.pdf"
 *   mimeType="application/pdf"
 * />
 */
/**
 * SpreadsheetPreview sub-component for Excel/CSV files.
 * Renders structured spreadsheet data as an HTML table with
 * a sticky header row and scrollable body.
 */
function SpreadsheetPreview({ data, format }) {
  const { headers, dataRows, metadata, sheetName, totalSheets } = data;
  const ROW_LIMIT = 200;
  const [showAll, setShowAll] = useState(false);

  const visibleRows = showAll ? dataRows : dataRows.slice(0, ROW_LIMIT);
  const hasMore = dataRows.length > ROW_LIMIT;
  const maxDataCols = dataRows.reduce((max, row) => Math.max(max, row.length), 0);
  const colCount = Math.max(headers.length, maxDataCols);

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0">
      {metadata && metadata.length > 0 && (
        <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">Document Header</div>
          {metadata.map((row, i) => {
            const nonEmpty = row.filter(c => c && c.toString().trim());
            if (nonEmpty.length === 0) return null;
            return (
              <div key={i} className="text-sm text-gray-700 leading-relaxed">
                {nonEmpty.join('  \u00B7  ')}
              </div>
            );
          })}
        </div>
      )}

      {format === 'excel' && sheetName && (
        <div className="flex-shrink-0 px-4 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
          <span className="text-xs text-gray-500">Sheet:</span>
          <span className="text-xs text-gray-800 font-medium">{sheetName}</span>
          {totalSheets > 1 && (
            <span className="text-xs text-gray-400">({totalSheets} sheets total)</span>
          )}
        </div>
      )}

      <div className="flex-1 overflow-auto min-h-0 min-w-0">
        <table className="border-collapse">
          {headers.length > 0 && (
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 bg-gray-100 border-b border-r border-gray-300 w-12">#</th>
                {headers.map((header, colIdx) => (
                  <th
                    key={colIdx}
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100 border-b border-r border-gray-300 whitespace-nowrap"
                  >
                    {header || `Col ${colIdx + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {visibleRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                <td className="px-3 py-1.5 text-xs text-gray-400 border-b border-r border-gray-200 w-12">{rowIdx + 1}</td>
                {Array.from({ length: colCount }, (_, colIdx) => {
                  const value = row[colIdx];
                  const display = value !== null && value !== undefined ? value.toString() : '';
                  return (
                    <td
                      key={colIdx}
                      className="px-3 py-1.5 text-sm text-gray-700 border-b border-r border-gray-200 whitespace-nowrap"
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex-shrink-0 px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <span>{dataRows.length} row{dataRows.length !== 1 ? 's' : ''}</span>
        {hasMore && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="text-blue-600 hover:text-blue-800"
          >
            Show all {dataRows.length} rows
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * TextPreview sub-component for non-visual files.
 * Fetches a text representation from the backend and displays it.
 * For Excel/CSV files, renders a visual spreadsheet table.
 */
function TextPreview({ objectKey, filename, detectedFileType, category, url }) {
  const [textContent, setTextContent] = useState(null);
  const [spreadsheetData, setSpreadsheetData] = useState(null);
  const [previewFormat, setPreviewFormat] = useState(null);
  const [textLoading, setTextLoading] = useState(true);
  const [textError, setTextError] = useState(null);

  useEffect(() => {
    if (!objectKey) return;
    let cancelled = false;
    setTextLoading(true);
    setTextError(null);

    getPreviewText(objectKey)
      .then(res => {
        if (!cancelled) {
          setTextContent(res.data.text);
          setPreviewFormat(res.data.format);
          setSpreadsheetData(res.data.spreadsheet || null);
          setTextLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTextError('Failed to load file preview');
          setTextLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [objectKey]);

  const typeLabels = {
    edi: 'EDI Document',
    tabular: 'Spreadsheet',
    text: 'Text Document',
  };
  const subtype = detectedFileType?.description || typeLabels[category] || 'Document';

  const isSpreadsheet = spreadsheetData
    && (previewFormat === 'excel' || previewFormat === 'csv')
    && (spreadsheetData.headers?.length > 0 || spreadsheetData.dataRows?.length > 0);

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-700 truncate">{filename || 'Document'}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">{subtype}</span>
        </div>
        {url && (
          <a
            href={url}
            download={filename}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </a>
        )}
      </div>

      {textLoading && (
        <div className="flex-1 flex items-center justify-center bg-gray-900">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
        </div>
      )}
      {textError && (
        <div className="flex-1 flex items-center justify-center bg-gray-900 text-red-400 text-sm">
          {textError}
        </div>
      )}

      {!textLoading && !textError && isSpreadsheet && (
        <SpreadsheetPreview data={spreadsheetData} format={previewFormat} />
      )}

      {!textLoading && !textError && !isSpreadsheet && textContent && (
        <div className="flex-1 overflow-auto bg-gray-900 p-4">
          <pre className="text-sm text-gray-200 font-mono whitespace-pre-wrap break-words leading-relaxed">
            {textContent}
          </pre>
        </div>
      )}
    </div>
  );
}

function DocumentViewer({ url, mimeType, detectedFileType, filename, objectKey }) {
  // PDF-specific state
  const [numPages, setNumPages] = useState(null);      // Total number of pages
  const [pageNumber, setPageNumber] = useState(1);     // Current page
  const [scale, setScale] = useState(1.0);             // Zoom level
  const [loading, setLoading] = useState(true);        // Loading state
  const [error, setError] = useState(null);            // Error state

  // Check if the document is a PDF
  const isPDF = mimeType === 'application/pdf';

  /**
   * Called when PDF document loads successfully
   * Sets the total number of pages
   */
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }, []);

  /**
   * Called when PDF fails to load
   */
  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF document');
    setLoading(false);
  }, []);

  /**
   * Navigate to previous page
   */
  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  /**
   * Navigate to next page
   */
  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(numPages || 1, prev + 1));
  };

  /**
   * Zoom in (increase scale)
   */
  const zoomIn = () => {
    setScale((prev) => Math.min(2.5, prev + 0.1));
  };

  /**
   * Zoom out (decrease scale)
   */
  const zoomOut = () => {
    setScale((prev) => Math.max(0.5, prev - 0.1));
  };

  /**
   * Reset zoom to 100%
   */
  const resetZoom = () => {
    setScale(1.0);
  };

  // No document loaded yet
  if (!url) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 text-gray-500">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="mt-2">No document loaded</p>
        </div>
      </div>
    );
  }

  // Check if the file is a non-visual type (Excel, EDI, CSV, text)
  const isImage = mimeType && mimeType.startsWith('image/');
  const category = detectedFileType?.category;
  const isNonVisual = category === 'edi' || category === 'tabular' || category === 'text';

  // For non-visual file types, fetch text representation and display it
  if (!isPDF && (isNonVisual || (!isImage && !mimeType?.startsWith('image/')))) {
    return (
      <TextPreview
        objectKey={objectKey}
        filename={filename}
        detectedFileType={detectedFileType}
        category={category}
        url={url}
      />
    );
  }

  // Render image directly for image documents
  if (!isPDF) {
    return (
      <div className="h-full overflow-auto p-4 bg-gray-100">
        <img
          src={url}
          alt="Purchase Order Document"
          className="max-w-full h-auto mx-auto shadow-lg"
          onLoad={() => setLoading(false)}
          onError={() => setError('Failed to load image')}
        />
      </div>
    );
  }

  // Render PDF viewer
  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 bg-white border-b border-gray-200 shadow-sm">
        {/* Page Navigation */}
        <div className="flex items-center space-x-2">
          <button
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {pageNumber} of {numPages || '?'}
          </span>
          <button
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={zoomOut}
            className="px-2 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
            title="Zoom out"
          >
            −
          </button>
          <button
            onClick={resetZoom}
            className="px-2 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
            title="Reset zoom"
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            onClick={zoomIn}
            className="px-2 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
            title="Zoom in"
          >
            +
          </button>
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-auto p-4">
        {/* Error State */}
        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-500">
              <p>{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {/* PDF Document */}
        {!error && (
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={false}      // Disable text layer for performance
              renderAnnotationLayer={false} // Disable annotation layer
              className="mx-auto shadow-lg"
            />
          </Document>
        )}
      </div>
    </div>
  );
}

export default DocumentViewer;
